import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import api, { setEmulationSessionId } from '../services/api'
import { useAuth } from './AuthContext'

const EmulationContext = createContext(null)

// Storage key for persisting emulation session across refreshes
const EMULATION_STORAGE_KEY = 'coformit_emulation_session'

export const EmulationProvider = ({ children }) => {
  const { user, profile, hasCapability } = useAuth()
  const [emulationSession, setEmulationSession] = useState(null)
  const [emulatedUser, setEmulatedUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Track if user was ever defined to distinguish initial load from logout
  const wasUserEverDefined = useRef(false)
  if (user) {
    wasUserEverDefined.current = true
  }

  // Check if current user can emulate (Super Admin, Tenant Admin, or has emulate_users capability)
  const canEmulate = user?.isSuperAdmin || (profile?.role === 'admin' && !profile?.isPlatformAdmin) || hasCapability('emulate_users')

  // Update the API module with the current session ID whenever it changes
  useEffect(() => {
    if (emulationSession && emulationSession.status === 'active') {
      setEmulationSessionId(emulationSession.sessionId)
    } else {
      setEmulationSessionId(null)
    }
  }, [emulationSession])

  // Clear emulation storage when user actually logs out (not on initial page load)
  // This prevents stale sessions from persisting across user changes
  useEffect(() => {
    if (!user) {
      // Only clear if user was previously logged in (actual logout, not initial load)
      if (wasUserEverDefined.current) {
        setEmulationSession(null)
        setEmulatedUser(null)
        sessionStorage.removeItem(EMULATION_STORAGE_KEY)
        setEmulationSessionId(null)
      }
    } else {
      // User is logged in - check if stored session belongs to this user
      const storedSession = sessionStorage.getItem(EMULATION_STORAGE_KEY)
      if (storedSession) {
        try {
          const parsed = JSON.parse(storedSession)
          if (parsed.actorUserId !== user.userId) {
            sessionStorage.removeItem(EMULATION_STORAGE_KEY)
          }
        } catch (e) {
          // Invalid JSON - clear it
          sessionStorage.removeItem(EMULATION_STORAGE_KEY)
        }
      }
    }
  }, [user?.userId])

  // Check for existing active session on mount
  useEffect(() => {
    const checkActiveSession = async () => {
      if (!user || !canEmulate) {
        setLoading(false)
        return
      }

      try {
        // Check sessionStorage for persisted session
        const storedSession = sessionStorage.getItem(EMULATION_STORAGE_KEY)
        if (storedSession) {
          const parsed = JSON.parse(storedSession)
          const response = await api.get('/emulation/sessions/active')
          if (response.data.session && response.data.session.sessionId === parsed.sessionId) {
            setEmulationSession(response.data.session)
            setEmulatedUser(response.data.emulatedUser)
          } else {
            sessionStorage.removeItem(EMULATION_STORAGE_KEY)
            setEmulationSession(null)
            setEmulatedUser(null)
          }
        } else {
          const response = await api.get('/emulation/sessions/active')
          if (response.data.session) {
            setEmulationSession(response.data.session)
            setEmulatedUser(response.data.emulatedUser)
            // Persist to storage
            sessionStorage.setItem(EMULATION_STORAGE_KEY, JSON.stringify(response.data.session))
          }
        }
      } catch (err) {
        sessionStorage.removeItem(EMULATION_STORAGE_KEY)
        // Reset state on error
        setEmulationSession(null)
        setEmulatedUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkActiveSession()
  }, [user, canEmulate])

  /**
   * Request to emulate another user
   * @param {string} targetUserId - The user ID to emulate
   * @returns {Promise<{success: boolean, session?: object, requiresApproval?: boolean, error?: string}>}
   */
  const requestEmulation = useCallback(async (targetUserId) => {
    setError(null)
    try {
      const response = await api.post('/emulation/request', { targetUserId })
      const { session, requiresApproval, message } = response.data

      // If no approval needed, we can start immediately
      if (!requiresApproval && session?.status === 'approved') {
        const startResponse = await api.post(`/emulation/sessions/${session.sessionId}/start`)
        setEmulationSession(startResponse.data.session)
        setEmulatedUser(startResponse.data.emulatedUser)
        sessionStorage.setItem(EMULATION_STORAGE_KEY, JSON.stringify(startResponse.data.session))
        return { success: true, session: startResponse.data.session, message: startResponse.data.message }
      }

      // Approval required - return the pending session
      return { success: true, session, requiresApproval, message }
    } catch (err) {
      const errorMessage = err.message || 'Failed to request emulation'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  /**
   * Approve an emulation request (for the target user)
   * @param {string} sessionId - The session ID to approve
   */
  const approveEmulation = useCallback(async (sessionId) => {
    setError(null)
    try {
      const response = await api.post(`/emulation/sessions/${sessionId}/approve`)
      return { success: true, session: response.data.session, message: response.data.message }
    } catch (err) {
      const errorMessage = err.message || 'Failed to approve emulation'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  /**
   * Deny an emulation request (for the target user)
   * @param {string} sessionId - The session ID to deny
   */
  const denyEmulation = useCallback(async (sessionId) => {
    setError(null)
    try {
      const response = await api.post(`/emulation/sessions/${sessionId}/deny`)
      return { success: true, session: response.data.session, message: response.data.message }
    } catch (err) {
      const errorMessage = err.message || 'Failed to deny emulation'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  /**
   * Start an approved emulation session
   * @param {string} sessionId - The session ID to start
   */
  const startEmulation = useCallback(async (sessionId) => {
    setError(null)
    try {
      const response = await api.post(`/emulation/sessions/${sessionId}/start`)
      setEmulationSession(response.data.session)
      setEmulatedUser(response.data.emulatedUser)
      sessionStorage.setItem(EMULATION_STORAGE_KEY, JSON.stringify(response.data.session))
      return { success: true, session: response.data.session, message: response.data.message }
    } catch (err) {
      const errorMessage = err.message || 'Failed to start emulation'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  /**
   * End the current emulation session
   */
  const endEmulation = useCallback(async () => {
    if (!emulationSession) {
      return { success: false, error: 'No active emulation session' }
    }

    setError(null)
    const sessionId = emulationSession.sessionId

    try {
      const response = await api.post(`/emulation/sessions/${sessionId}/end`)
      sessionStorage.removeItem(EMULATION_STORAGE_KEY)
      setEmulationSession(null)
      setEmulatedUser(null)

      return { success: true, message: response.data.message }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to end emulation'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [emulationSession])

  /**
   * Clear emulation state (used when logging out)
   */
  const clearEmulation = useCallback(() => {
    setEmulationSession(null)
    setEmulatedUser(null)
    sessionStorage.removeItem(EMULATION_STORAGE_KEY)
  }, [])

  const value = {
    // State
    emulationSession,
    emulatedUser,
    isEmulating: !!emulationSession && emulationSession.status === 'active',
    loading,
    error,
    canEmulate,

    // Actions
    requestEmulation,
    approveEmulation,
    denyEmulation,
    startEmulation,
    endEmulation,
    clearEmulation,
  }

  return <EmulationContext.Provider value={value}>{children}</EmulationContext.Provider>
}

export const useEmulation = () => {
  const context = useContext(EmulationContext)
  if (!context) {
    throw new Error('useEmulation must be used within an EmulationProvider')
  }
  return context
}

export default EmulationContext
